<script setup lang="ts">
import { ActivitySummary, PhaseTemplate, Phase } from '@beeboard/common';
import { requiredIf } from '@vuelidate/validators';
import { isNil } from 'lodash-es';
import { useProjectStore } from '@/store/projectStore';
import { useUiStore } from '@/store/uiStore';
import { plusXDays } from '@/utils/helpers/datetime';

export interface SubmitData {
  projectPhaseId: string;
  templatePhaseId: string;
  deadlines: string[];
}

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'cancel'): void;
  (e: 'submit', data: SubmitData): void;
}>();

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  startDate: {
    type: String,
    required: true,
  },
  projectId: {
    type: String,
    required: true,
  },
  templatePhases: {
    type: Array as PropType<PhaseTemplate[]>,
    required: true,
  },
  projectPhases: {
    type: Array as PropType<Phase[]>,
    required: true,
  },
  projectActivities: {
    type: Array as PropType<ActivitySummary[]>,
    required: true,
  },
});

const formRows = computed(() =>
  Array(Math.max(props.templatePhases.length, props.projectPhases.length)),
);

const formData = reactive({
  deadlines: Array(props.templatePhases.length).fill(null),
});

const projectPhaseIndex = ref<number>(-1);
const templatePhaseIndex = ref<number>(-1);

watch([projectPhaseIndex, templatePhaseIndex], () => {
  if (projectPhaseIndex.value >= 0 && templatePhaseIndex.value >= 0) {
    const startIndex = templatePhaseIndex.value;
    const selectedTemplatePhases = props.templatePhases.length;
    const selectedProjectPhases = props.projectPhases.slice(
      projectPhaseIndex.value,
    );

    for (let i = 0; i < startIndex; i++) {
      formData.deadlines[i] = null;
    }
    for (let i = startIndex; i < selectedTemplatePhases; i++) {
      const templatePhase = props.templatePhases[i];

      const matchingPhase = selectedProjectPhases.find(
        (ph) => ph.title === templatePhase.title,
      );

      if (matchingPhase) {
        formData.deadlines[i] = new Date(
          matchingPhase.final_milestone.current_deadline,
        );
      } else {
        formData.deadlines[i] = null;
      }
    }
  } else {
    formData.deadlines = Array(4).fill(null);
  }
});

const rules = {
  deadlines: formData.deadlines.map((_, index) => {
    const isEnabled = () => {
      const isDisabled =
        templatePhaseIndex.value > index ||
        templatePhaseIndex.value === -1 ||
        projectPhaseIndex.value === -1;

      return !isDisabled;
    };

    return [requiredIf(isEnabled)];
  }),
};

const minDates = computed(() => {
  const dates: string[] = [];

  if (templatePhaseIndex.value === -1 || projectPhaseIndex.value === -1) {
    return dates;
  }

  const startDate =
    projectPhaseIndex.value === 0
      ? props.startDate
      : props.projectPhases[projectPhaseIndex.value - 1].final_milestone
          .current_deadline;

  const startIndex = templatePhaseIndex.value;

  for (let i = startIndex; i < props.templatePhases.length; i++) {
    if (i === startIndex) {
      dates[i] = plusXDays(startDate, 2).toISO();
    } else {
      const prevDeadline = formData.deadlines[i - 1];
      const prevDate = prevDeadline ? prevDeadline.toISOString() : dates[i - 1];
      dates[i] = plusXDays(prevDate, 2).toISO();
    }
  }

  return dates;
});

const maxDates = computed(() => {
  const dates: Array<string | undefined> = [];

  if (templatePhaseIndex.value === -1 || projectPhaseIndex.value === -1) {
    return dates;
  }

  for (let i = props.templatePhases.length - 1; i >= 0; i--) {
    if (i === props.templatePhases.length - 1) {
      dates[i] = undefined;
    } else {
      const nextDeadline = formData.deadlines[i + 1];
      const nextDate = nextDeadline ? nextDeadline.toISOString() : dates[i + 1];
      dates[i] = nextDate ? plusXDays(nextDate, -2).toISO() : undefined;
    }
  }

  return dates;
});

const hasActivePhases = computed(() => {
  if (projectPhaseIndex.value < 0) {
    return false;
  }
  const phases = props.projectPhases.slice(projectPhaseIndex.value);

  return phases.some((phase) => ['underway', 'done'].includes(phase.state));
});

const hasActiveActivities = computed(() => {
  if (projectPhaseIndex.value < 0) {
    return false;
  }
  const phases = props.projectPhases.slice(projectPhaseIndex.value);
  const phaseIds = phases.map((phase) => phase.id);

  const activities = props.projectActivities.filter((activity) =>
    phaseIds.includes(activity.phase),
  );

  return activities.some((activity) =>
    ['underway', 'done'].includes(activity.state),
  );
});

const warningMessage = computed(() => {
  if (hasActiveActivities.value) {
    return $t('projectUpdateFromTemplateModal.warningTitleActivities');
  }
  if (hasActivePhases.value) {
    return $t('projectUpdateFromTemplateModal.warningTitlePhases');
  }

  return '';
});

function isCheckboxSelected(index: number, name: 'project' | 'template') {
  if (name === 'project') {
    if (projectPhaseIndex.value < 0) {
      return false;
    }

    return index >= projectPhaseIndex.value;
  }
  if (name === 'template') {
    if (templatePhaseIndex.value < 0) {
      return false;
    }

    return index >= templatePhaseIndex.value;
  }

  return false;
}

function toggleCheckbox(index: number, name: 'project' | 'template') {
  if (name === 'project') {
    const totalPhases = props.projectPhases.length;

    if (projectPhaseIndex.value === -1) {
      projectPhaseIndex.value = index;
    } else if (index >= projectPhaseIndex.value) {
      projectPhaseIndex.value = index === totalPhases - 1 ? -1 : index + 1;
    } else {
      projectPhaseIndex.value = index;
    }
  }

  if (name === 'template') {
    const totalPhases = props.templatePhases.length;

    if (templatePhaseIndex.value === -1) {
      templatePhaseIndex.value = index;
    } else if (index >= templatePhaseIndex.value) {
      templatePhaseIndex.value = index === totalPhases - 1 ? -1 : index + 1;
    } else {
      templatePhaseIndex.value = index;
    }
  }
}

function formatDate(date: string) {
  return date.split('T')[0].split('-').reverse().join('.');
}

async function handleSubmit() {
  let confirmResult: boolean | string = true;

  const projectName = useProjectStore().project(props.projectId).title;

  if (hasActiveActivities.value || hasActivePhases.value) {
    confirmResult = await useUiStore().confirm(
      $t('projectUpdateFromTemplateModal.projectWillBeUpdated'),
      {
        kind: 'danger',
        subtitle: $t(
          'projectUpdateFromTemplateModal.projectWillBeUpdatedSubtitle',
        ),
        description: $t('project.pleaseTypeInProjectName'),
        yesText: $t('button.update'),
        requiredAnswer: projectName,
      },
    );
  }

  if (!projectName) {
    return;
  }

  if (typeof confirmResult === 'string' && projectName !== confirmResult) {
    return;
  }

  const projPhase = props.projectPhases[projectPhaseIndex.value];
  const tempPhase = props.templatePhases[templatePhaseIndex.value];
  const data: SubmitData = {
    deadlines: formData.deadlines.filter((dl) => !isNil(dl)),
    projectPhaseId: projPhase.id,
    templatePhaseId: tempPhase.id,
  };
  emit('submit', data);
}
</script>
<template>
  <div class="header">
    <p>{{ $t('projectUpdateFromTemplateModal.selectPhases') }}</p>
    <p>
      {{ $t('project.startDate') }}:
      <b>{{ formatDate(props.startDate) }}</b>
    </p>
  </div>
  <UiAlert
    v-if="!!warningMessage"
    variant="warning"
    :title="$t('dialog.attentionNeeded')"
    :content="warningMessage"
    is-wide
  />
  <div class="list-header">
    <div class="header-item">
      <span class="header-title">{{
        $t('projectUpdateFromTemplateModal.projectPhases')
      }}</span>
    </div>
    <div class="header-item">
      <span class="header-title">{{
        $t('projectUpdateFromTemplateModal.templatePhases')
      }}</span>
    </div>
    <div class="header-item-milestones">
      <span class="header-title">{{
        $t('projectUpdateFromTemplateModal.finalMilestones')
      }}</span>
    </div>
  </div>
  <UiForm :rules="rules" :form-data="formData" @form-submit="handleSubmit">
    <template #default="{ formErrors }">
      <div class="phases">
        <div v-for="(_, index) in formRows" :key="index" class="row">
          <!-- Project Phases -->
          <div v-if="!!projectPhases[index]" class="phase-item">
            <UiCheckbox
              class="checkbox"
              name="projectPhase"
              :model-value="isCheckboxSelected(index, 'project')"
              @update:model-value="toggleCheckbox(index, 'project')"
            />
            <div class="phase">
              <span class="phase-title">{{ projectPhases[index].title }}</span>
              <br />
              <span class="ms-title">
                {{ projectPhases[index].final_milestone.title }}
                ({{
                  formatDate(
                    projectPhases[index].final_milestone.current_deadline,
                  )
                }})
              </span>
            </div>
          </div>
          <div v-else class="phase-item">
            <!-- render empty div if phase element is missing -->
          </div>
          <!-- Template Phases -->
          <div v-if="!!templatePhases[index]" class="phase-item">
            <UiCheckbox
              class="checkbox"
              name="templatePhase"
              :model-value="isCheckboxSelected(index, 'template')"
              @update:model-value="toggleCheckbox(index, 'template')"
            />
            <div class="phase">
              <span class="phase-title">{{ templatePhases[index].title }}</span>
              <br />
              <span class="ms-title">
                {{ templatePhases[index].final_milestone.title }}
              </span>
            </div>
          </div>
          <!-- Final Milestones -->
          <div v-if="!!templatePhases[index]">
            <UiField :errors="formErrors['deadlines.' + index]">
              <UIDatePicker
                id="start-date"
                v-model="formData.deadlines[index]"
                name="deadlines"
                :min-date="minDates[index]"
                :max-date="maxDates[index]"
                :disabled="
                  templatePhaseIndex > index ||
                  templatePhaseIndex === -1 ||
                  projectPhaseIndex === -1
                "
              />
            </UiField>
          </div>
        </div>
      </div>
    </template>
    <template #bottom="{ hasErrors, formSubmit }">
      <div class="actions">
        <UiButtonRow
          size="medium"
          :cancel-button-text="$t('button.back')"
          :action-button-text="$t('button.update')"
          :disabled="
            hasErrors ||
            loading ||
            templatePhaseIndex === -1 ||
            projectPhaseIndex === -1
          "
          has-space-between
          @cancel="emit('back')"
          @action="formSubmit"
        />
      </div>
    </template>
  </UiForm>
</template>

<style scoped lang="scss">
.header {
  display: flex;
  justify-content: space-between;
  color: var(--color-gray-500);
  margin: var(--space-12) 0 var(--space-24);
}

.list-header {
  display: flex;
  justify-content: space-between;
  line-height: var(--line-height-28);
  border-bottom: 1px #eee solid;
  padding: var(--space-12);
}

.phases {
  min-height: px(310);
  max-height: calc(100vh - 400px);
  overflow-y: auto;
}

.row {
  display: flex;
  justify-content: space-between;
  line-height: var(--line-height-28);
  border-bottom: 1px #eee solid;
  min-height: px(72);
  padding: var(--space-12);
}

.header-item,
.phase-item {
  display: flex;
  flex: 1;
  align-items: end;
}
.header-item-milestones {
  width: px(244);
}
.phase {
  margin-left: var(--space-10);
}

.header-title,
.phase-title {
  font-size: var(--font-size-18);
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-500);
}

.ms-title {
  font-size: var(--font-size-16);
  font-weight: var(--font-weight-regular);
  color: var(--color-gray-600);
}

.checkbox {
  line-height: normal;
  margin-bottom: var(--space-6);
}

.actions {
  margin-top: var(--space-24);
}
</style>
